/**
 * Fill `toolResultStore` from the tool results in the transcript — what the inline
 * `sphere-handle:` link renderer reads. sphere-mcp executes every tool
 * (MDOT-25078) and sends the client the whole MCP result as the tool part's `output`,
 * envelope included, so the store is filled off the transcript, live or restored alike.
 *
 * Idempotent: keyed by tool-call id, so re-running over a growing transcript rewrites the
 * same entries with the same values, which is what lets the caller be a plain effect.
 */
import { isToolUIPart, type UIMessage } from "ai";
import { setToolResult } from "./toolResultStore";

/** An MCP tool result as sphere-mcp returns it, reduced to the halves read here. */
type McpResultish = {
	structuredContent?: unknown;
};

/**
 * Read every settled tool result out of `messages` and record its structured half. Only
 * `output-available` parts: a call still streaming has no result, an errored one no payload.
 */
export function absorbServerToolResults(messages: ReadonlyArray<UIMessage>): void {
	for (const message of messages) {
		if (message.role !== "assistant") continue;
		for (const part of message.parts) {
			if (!isToolUIPart(part) || part.state !== "output-available") continue;
			const result = part.output as McpResultish | undefined;
			if (typeof result !== "object" || result === null) continue;
			// A no-op without `structuredContent`, so non-MCP tools need no exclusion here.
			setToolResult(part.toolCallId, result.structuredContent);
		}
	}
}
